/**
 * TPO-specific Conditional Logger
 * Controls logging based on window variable flags for Trade Promo Optimization
 */

// Extend Window interface to include our custom logging flags
declare global {
    interface Window {
        __TPO_DEBUG__?: boolean;
        __TPO_FORMULA_DEBUG__?: boolean;
        __TPO_KPI_DEBUG__?: boolean;
    }
}

interface LogConfig {
    timestamp?: boolean;
    module?: string;
    color?: string;
}

class TPOConditionalLogger {
    private static instance: TPOConditionalLogger;

    public static getInstance(): TPOConditionalLogger {
        if (!TPOConditionalLogger.instance) {
            TPOConditionalLogger.instance = new TPOConditionalLogger();
        }
        return TPOConditionalLogger.instance;
    }

    private isDebugEnabled(flag?: keyof Window): boolean {
        if (flag && typeof window !== 'undefined') {
            return Boolean(window[flag]);
        }
        return typeof window !== 'undefined' && Boolean(window.__TPO_DEBUG__);
    }

    private formatLog(level: string, message: string, data: any, config: LogConfig = {}): void {
        if (typeof window === 'undefined') return;

        const timestamp = config.timestamp !== false ? new Date().toISOString() : '';
        const module = config.module ? `[${config.module}]` : '';
        const color = config.color || '#2563eb';

        const prefix = `%c${timestamp} ${module} [${level.toUpperCase()}]`;
        const style = `color: ${color}; font-weight: bold;`;

        if (data !== undefined) {
            console.groupCollapsed(`${prefix} ${message}`, style);
            console.log('Data:', data);
            console.groupEnd();
        } else {
            console.log(`${prefix} ${message}`, style);
        }
    }

    public debug(message: string, data?: any, config: LogConfig = {}): void {
        if (this.isDebugEnabled()) {
            this.formatLog('debug', message, data, { ...config, color: '#6b7280' });
        }
    }

    public info(message: string, data?: any, config: LogConfig = {}): void {
        if (this.isDebugEnabled()) {
            this.formatLog('info', message, data, { ...config, color: '#2563eb' });
        }
    }

    public warn(message: string, data?: any, config: LogConfig = {}): void {
        if (this.isDebugEnabled()) {
            this.formatLog('warn', message, data, { ...config, color: '#f59e0b' });
        }
    }

    public error(message: string, data?: any, config: LogConfig = {}): void {
        if (this.isDebugEnabled()) {
            this.formatLog('error', message, data, { ...config, color: '#ef4444' });
        }
    }

    // Specialized logging methods for different TPO modules
    public formula(message: string, data?: any, config: LogConfig = {}): void {
        if (this.isDebugEnabled('__TPO_FORMULA_DEBUG__')) {
            this.formatLog('formula', message, data, {
                ...config,
                color: '#8b5cf6',
                module: config.module || 'Formula Engine',
            });
        }
    }

    public kpi(message: string, data?: any, config: LogConfig = {}): void {
        if (this.isDebugEnabled('__TPO_KPI_DEBUG__')) {
            this.formatLog('kpi', message, data, {
                ...config,
                color: '#10b981',
                module: config.module || 'KPI Calculator',
            });
        }
    }

    // Method to show formula evaluation in a table format
    public formulaEvaluation(formula: string, variables: Record<string, any>, result: any): void {
        if (this.isDebugEnabled('__TPO_FORMULA_DEBUG__')) {
            console.group(`%c Formula Evaluation`, 'color: #8b5cf6; font-weight: bold; font-size: 14px;');
            console.log(`%cFormula: %c${formula}`, 'font-weight: bold;', 'color: #4f46e5;');
            console.table(variables);
            console.log(`%cResult: %c${result}`, 'font-weight: bold;', 'color: #059669; font-size: 12px;');
            console.groupEnd();
        }
    }

    // Method to show KPI dependency chain
    public kpiDependencyChain(kpi: string, dependents: string[], level: number = 0): void {
        if (this.isDebugEnabled('__TPO_KPI_DEBUG__')) {
            const indent = '  '.repeat(level);
            const arrow = level > 0 ? '└─ ' : '';
            console.log(
                `%c${indent}${arrow}${kpi} ${dependents.length > 0 ? `→ [${dependents.join(', ')}]` : '(leaf)'}`,
                'color: #10b981; font-family: monospace;',
            );
        }
    }
}

// Export singleton instance
export const tpoLogger = TPOConditionalLogger.getInstance();

// Helper function to enable/disable logging flags
export const setTPODebugFlags = (flags: { general?: boolean; formula?: boolean; kpi?: boolean }) => {
    if (typeof window !== 'undefined') {
        if (flags.general !== undefined) window.__TPO_DEBUG__ = flags.general;
        if (flags.formula !== undefined) window.__TPO_FORMULA_DEBUG__ = flags.formula;
        if (flags.kpi !== undefined) window.__TPO_KPI_DEBUG__ = flags.kpi;

        console.log('%c🔧 TPO Debug Flags Updated:', 'color: #3b82f6; font-weight: bold;', {
            general: window.__TPO_DEBUG__,
            formula: window.__TPO_FORMULA_DEBUG__,
            kpi: window.__TPO_KPI_DEBUG__,
        });
    }
};

// Helper function to show current debug status
export const getTPODebugStatus = () => {
    if (typeof window !== 'undefined') {
        return {
            general: Boolean(window.__TPO_DEBUG__),
            formula: Boolean(window.__TPO_FORMULA_DEBUG__),
            kpi: Boolean(window.__TPO_KPI_DEBUG__),
        };
    }
    return { general: false, formula: false, kpi: false };
};

// Make helper functions available globally for console access
if (typeof window !== 'undefined') {
    (window as any).setTPODebugFlags = setTPODebugFlags;
    (window as any).getTPODebugStatus = getTPODebugStatus;
}
